import { Calendar, Camera, Mail, Sparkles, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useAppShell } from "@/shell/app-shell-context";
import { apiClient } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ProfileAvatar } from "@/shared/components/ProfileAvatar";
import { AvatarImageError, squareAvatar } from "@/shared/utils/avatar-image";
import { ROLE_LABEL, ROLES, type Role } from "@/shared/utils/roles";
// O rótulo vem do vocabulário compartilhado (`ROLE_LABEL`): até 2026-10-09
// esta tela tinha um mapa próprio no dialeto órfão do backend descartado
// (`admin`, `master_admin`, só `analyst` dos quatro), e Coordenador e
// Técnico apareciam como "Usuário", com o código cru em inglês na aba de
// permissões (teste de render). As descrições seguem a proposta de IAM
// (revisão de 2026-10-22, que renomeou os cargos e criou o Visualizador).
const ROLE_DESCRIPTIONS: Record<Role, string> = {
  master:
    "Acesso a tudo: todas as empresas e núcleos, configuração geral do CENA e gestão de todos os usuários.",
  administrator:
    "Todas as empresas do seu núcleo; cria empresas e gere os usuários do núcleo abaixo do seu cargo.",
  specialist:
    "Empresas atribuídas a você; altera as configurações da empresa, inclusive o CENA dela, e atualiza os dados de amostras salvas.",
  technician:
    "Edita classificações, gera e salva amostras, exporta dados e acessa o dashboard; não altera a configuração da empresa.",
  viewer:
    "Só visualiza: menções, dashboard, amostras salvas e configurações das empresas atribuídas."
};

function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

function roleLabel(value: string): string {
  return isRole(value) ? ROLE_LABEL[value] : "Usuário";
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

export type ProfileTab = "identity" | "companies" | "permissions";

/**
 * As três seções de perfil de Configurações (Identidade, Empresas vinculadas,
 * Nível de acesso). Quem escolhe a seção é a barra lateral de Configurações;
 * cada uma é um painel no desenho dos de Ajustes (`ajustes-panel` +
 * `panel-header` + `panel-section`), pra todas as telas de conta terem o mesmo
 * ritmo de título, texto e espaçamento.
 *
 * Esta tela só MOSTRA nome e e-mail: são os do CADASTRO, o que o
 * administrador digitou no convite (decisão da dona, 2026-10-14), e quem os
 * corrige é ele. Até 2026-10-14 a tela encaminhava pro painel da conta no
 * Keycloak; a decisão é que só a TI vê o Keycloak. A foto é do ReputAItion
 * (2026-10-15): a pessoa troca aqui, e a tela a reduz a 256×256 antes de
 * mandar. Até 2026-10-11 havia campo de nome, exportação e desativação
 * chamando `/auth/*`, rotas do backend descartado que o `web_api` nunca teve.
 */
export function ProfilePage({ tab = "identity" }: { tab?: ProfileTab } = {}) {
  const { token, profile, refreshProfile } = useAppShell();
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const primaryRole = profile.roles[0] ?? "user";

  // Depois de gravar, a casca relê o perfil e a foto nova aparece no topo
  // também.
  const changePhoto = async (file: File) => {
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      await apiClient.uploadMyAvatar(token, await squareAvatar(file));
      await refreshProfile();
    } catch (cause) {
      setPhotoError(
        cause instanceof AvatarImageError
          ? cause.message
          : errorMessage(cause, "Não foi possível trocar a foto.")
      );
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      await apiClient.deleteMyAvatar(token);
      await refreshProfile();
    } catch (cause) {
      setPhotoError(errorMessage(cause, "Não foi possível remover a foto."));
    } finally {
      setPhotoBusy(false);
    }
  };

  if (tab === "companies") {
    return (
      <div className="ajustes-panel account-panel">
        <header className="panel-header">
          <div>
            <h2>Empresas vinculadas ({profile.companies.length})</h2>
            <p>Organizações e marcas em que você tem permissão de acesso.</p>
          </div>
        </header>
        {profile.companies.length > 0 ? (
          <ul className="account-list">
            {profile.companies.map(company => (
              <li key={company.id} className="company-badge-card">
                <span className="company-badge-icon">
                  {company.logo_url ? (
                    <img src={company.logo_url} alt={company.name} />
                  ) : (
                    <span>{company.name.charAt(0).toUpperCase()}</span>
                  )}
                </span>
                <span className="company-badge-info">
                  <strong>{company.name}</strong>
                  <span>{company.website || "Sem site cadastrado"}</span>
                </span>
                {/* Lê `is_active` de verdade: era texto fixo, e o
                    Máster recebe no `/me` também as empresas
                    desativadas (2026-10-08) — apareciam "Ativo". */}
                <span className={`status-pill ${company.is_active === false ? "inactive" : "active"}`}>
                  {company.is_active === false ? "Inativa" : "Ativa"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="account-note">Você ainda não está vinculado a nenhuma empresa no momento.</p>
        )}
      </div>
    );
  }

  if (tab === "permissions") {
    return (
      <div className="ajustes-panel account-panel">
        <header className="panel-header">
          <div>
            <h2>Nível de acesso</h2>
            <p>Seu cargo e o que ele permite no Reputaition.</p>
          </div>
        </header>
        <section className="panel-section">
          <h3>Cargo</h3>
          <div className="role-summary-box">
            <span className="account-list-icon"><Sparkles size={16} /></span>
            <div>
              <strong>{roleLabel(primaryRole)}</strong>
              <p>
                {isRole(primaryRole)
                  ? ROLE_DESCRIPTIONS[primaryRole]
                  : "Acesso padrão às funcionalidades da plataforma."}
              </p>
            </div>
          </div>
        </section>
        <section className="panel-section">
          <h3>Detalhes</h3>
          <ul className="account-list">
            <li className="account-row">
              <span className="account-list-icon"><Mail size={16} /></span>
              <span className="account-list-text"><span>E-mail cadastrado</span></span>
              <strong>{profile.email}</strong>
            </li>
            <li className="account-row">
              <span className="account-list-icon"><Calendar size={16} /></span>
              <span className="account-list-text"><span>Membro desde</span></span>
              <strong>{formatDate(profile.created_at)}</strong>
            </li>
          </ul>
        </section>
      </div>
    );
  }

  return (
    <div className="ajustes-panel account-panel">
      <header className="panel-header">
        <div>
          <h2>Identidade</h2>
          <p>Como você aparece no painel e nas amostras.</p>
        </div>
      </header>
      <section className="panel-section">
        <div className="account-identity">
          <div className="avatar identity-avatar account-identity-avatar">
            <ProfileAvatar name={profile.name} avatarUrl={profile.avatar_url} />
          </div>
          <div className="identity-summary">
            <strong>{profile.name}</strong>
            <span className="account-email">{profile.email}</span>
            <span className="role-pill">{roleLabel(primaryRole)}</span>
          </div>
        </div>

        <div className="card-actions identity-photo-actions">
          <input
            ref={photoInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            aria-label="Escolher foto"
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void changePhoto(file);
            }}
          />
          <button
            type="button"
            className="secondary-button"
            disabled={photoBusy}
            onClick={() => photoInput.current?.click()}
          >
            <Camera size={16} />
            {profile.avatar_url ? "Trocar foto" : "Adicionar foto"}
          </button>
          {profile.avatar_url && (
            <button
              type="button"
              className="secondary-button"
              disabled={photoBusy}
              onClick={() => void removePhoto()}
            >
              <Trash2 size={16} />
              Remover foto
            </button>
          )}
        </div>
        {photoError && (
          <p className="form-error-alert" role="alert">
            {photoError}
          </p>
        )}
      </section>
      <section className="panel-section">
        <h3>Dados da conta</h3>
        <p className="account-note">
          Nome e e-mail são os do seu cadastro no ReputAItion. Para corrigir o
          nome, ou para exportar ou excluir seus dados pessoais, fale com o
          administrador do seu núcleo ou com um Máster. A foto você troca aqui.
        </p>
      </section>
    </div>
  );
}
